import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Palette, Megaphone, BookOpen, Leaf, HeartPulse, Users, Heart, Shield, Scale, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { programs as mockPrograms } from '../../data/mock';
import { useContent } from '../../context/ContentContext';
import api from '../../lib/api';

const iconMap = {
  Palette: Palette,
  Megaphone: Megaphone,
  BookOpen: BookOpen,
  Leaf: Leaf,
  HeartPulse: HeartPulse,
  Users: Users,
  Scale: Scale,
  Shield: Shield,
  Heart: Heart,
};

const ProgramsPage = () => {
  const { c } = useContent();
  const [programs, setPrograms] = React.useState(mockPrograms);
  React.useEffect(() => {
    api.get('/programs').then((res) => { if (res.data?.length) setPrograms(res.data); }).catch(() => {});
  }, []);
  return (
    <main className="pt-52">
      {/* Hero Section */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
              {c('programs.hero.eyebrow', 'Our Work')}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-3 mb-6">
              {c('programs.hero.title', 'Programs & Initiatives')}
            </h1>
            <p className="text-lg text-[#3c4a2a]/80 leading-relaxed">
              {c('programs.hero.text', 'Our five focus areas align with the five GFWC community service pillars — Arts & Culture, Civic Engagement & Outreach, Education & Libraries, Environment, and Health & Wellness.')}
            </p>
            <p className="text-[#3c4a2a]/70 leading-relaxed mt-4 border-l-2 border-[#5d6b3c] pl-4" data-testid="gfwc-note">
              {c('programs.hero.gfwc_note', "Our program goals align with the General Federation of Women's Clubs (GFWC) in preparation for joining the General Federation after our initial development period.")}{' '}
              <Link to="/gfwc" className="text-[#5d6b3c] underline hover:text-[#3c4a2a]" data-testid="gfwc-note-link">
                Learn about GFWC
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Programs List */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {programs.map((program, index) => {
              const IconComponent = iconMap[program.icon] || Users;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={program.id}
                  id={program.id}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${isEven ? '' : 'lg:flex-row-reverse'}`}
                >
                  <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                      <img
                        src={program.image}
                        alt={program.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  
                  <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                    <div className="w-14 h-14 rounded-full bg-[#bd8cae]/30 flex items-center justify-center mb-6">
                      <IconComponent className="w-7 h-7 text-[#5d6b3c]" />
                    </div>
                    <h2 className="font-serif text-3xl text-[#3c4a2a] mb-4">
                      {program.title}
                    </h2>
                    <p className="text-[#3c4a2a]/80 leading-relaxed mb-6">
                      {program.description}
                    </p>

                    <div className="border-t border-[#bd8cae]/50 pt-4" data-testid={`program-goals-${program.id}`}>
                      <p className="text-sm font-medium text-[#3c4a2a] mb-3">Program Goals</p>
                      <ul className="space-y-2 text-[#3c4a2a]/70 text-sm">
                        {(program.goals || []).map((goal) => (
                          <li key={goal} className="flex items-start">
                            <ChevronRight className="w-4 h-4 text-[#bd8cae] mt-0.5 mr-2 flex-shrink-0" />
                            {goal}
                          </li>
                        ))}
                      </ul>
                      <p className="text-xs text-[#3c4a2a]/50 mt-4 italic">
                        Goals align with the General Federation of Women's Clubs (GFWC).
                      </p>
                    </div>

                    <Link to="/get-involved" className="inline-block mt-6">
                      <Button className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white">
                        Get Involved
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partner With Us CTA */}
      <section className="py-20 bg-[#5d6b3c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-6">
            Partner With Us
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Looking to make a meaningful community impact? We welcome partnerships with 
            organizations, businesses, and individuals who share our commitment to 
            women's leadership and community advancement.
          </p>
          <Link to="/contact">
            <Button className="bg-[#bd8cae] hover:bg-[#bd8cae] text-[#3c4a2a] px-8 py-6 text-lg">
              Let's Connect
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ProgramsPage;
